// Caixa de entrada omnichannel — Fase 3.
//
// Três colunas: fila de conversas, thread e contexto do contato. O envio (Fase
// 5) já existe no backend e está ligado aqui pelo composer.
//
// A Fase 14 acrescentou o que faz a caixa funcionar com várias pessoas dentro:
// responsável, situação, etiquetas, notas internas, respostas rápidas e a busca
// no corpo das mensagens (o ícone de lupa do cabeçalho, não o campo da lista —
// são buscas diferentes, ver `ModalBusca`).
//
// O que esta tela ainda NÃO faz: parear conexão por QR, que é a Fase 4.
//
// Acesso: quem manda é `inbox_members`, não `profiles.role`. Sem org o usuário
// vê uma explicação, não uma caixa vazia — caixa vazia parece defeito.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Inbox as InboxIcon, Loader2, RefreshCw, Search, Settings2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useInboxMembro } from '../../../src/hooks/useInboxMembro';
import { supabase } from '../../../src/lib/supabase';
import type {
  Chamado, ConexaoPublica, ConversaLista, ConversaStatus, Mensagem, PayloadBroadcast, TicketPrioridade, TicketStatus,
} from './tipos';
import {
  PAGINA_THREAD, carregarConexoes, carregarConversas, carregarMensagens, carregarOrgId,
  enviarMensagem, marcarComoLida, reenviarMensagem,
} from './api';
import { abrirChamado, carregarChamado, escalarParaTi, mudarStatusChamado } from './apiChamados';
import type { Etiqueta, Membro, RespostaRapida } from './apiOrganizacao';
import {
  carregarEtiquetas, carregarEtiquetasDasConversas, carregarMembrosDaOrg, carregarRespostasRapidas,
} from './apiOrganizacao';
import { useInboxRealtime } from './useInboxRealtime';
import { GRUPOS_DE_CANAL } from './formato';
import ModalAbrirChamado from './componentes/ModalAbrirChamado';
import ModalBusca from './componentes/ModalBusca';
import BannerConexao from './componentes/BannerConexao';
import Composer from './componentes/Composer';
import { criarAcoes, resumoCitacao, transporteInbox, useEnvioRico } from './envioRico';
import ListaConversas from './componentes/ListaConversas';
import PainelContato from './componentes/PainelContato';
import Thread from './componentes/Thread';

export default function InboxView() {
  const { profile } = useAuth();
  const { ehAdmin, papel } = useInboxMembro(profile?.id);
  // #6530: observador (gerente do Comercial) acompanha sem responder, sem mexer em
  // chamado e etiqueta e sem marcar como lida — a conversa continua não lida para o vendedor.
  // O banco e o inbox-send recusam a escrita do observador; aqui é só para não oferecer.
  const somenteLeitura = papel === 'observador';
  const somenteLeituraRef = useRef(somenteLeitura);
  somenteLeituraRef.current = somenteLeitura;

  const [orgId, setOrgId] = useState<string | null>(null);
  const [semAcesso, setSemAcesso] = useState(false);
  const [iniciando, setIniciando] = useState(true);
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  const [conexoes, setConexoes] = useState<ConexaoPublica[]>([]);
  const [conversas, setConversas] = useState<ConversaLista[]>([]);
  const [totalConversas, setTotalConversas] = useState(0);
  const [carregandoLista, setCarregandoLista] = useState(false);

  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [carregandoThread, setCarregandoThread] = useState(false);
  const [carregandoAntigas, setCarregandoAntigas] = useState(false);
  const [temMaisAntigas, setTemMaisAntigas] = useState(false);

  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [reenviandoId, setReenviandoId] = useState<string | null>(null);

  const [chamado, setChamado] = useState<Chamado | null>(null);
  const [ocupadoChamado, setOcupadoChamado] = useState(false);
  const [abrindoChamado, setAbrindoChamado] = useState(false);
  const [erroChamado, setErroChamado] = useState<string | null>(null);
  const [modalChamado, setModalChamado] = useState(false);

  // Fase 14 — o que a equipe usa para se organizar. Carregado uma vez por org:
  // são listas curtas que mudam raramente, e relê-las a cada conversa aberta
  // seria uma consulta por clique sem nenhum ganho.
  const [membros, setMembros] = useState<Membro[]>([]);
  const [etiquetas, setEtiquetas] = useState<Etiqueta[]>([]);
  const [respostasRapidas, setRespostasRapidas] = useState<RespostaRapida[]>([]);
  const [etiquetasPorConversa, setEtiquetasPorConversa] = useState<Record<string, string[]>>({});
  const [buscaAberta, setBuscaAberta] = useState(false);

  const [busca, setBusca] = useState('');
  const [buscaDebounce, setBuscaDebounce] = useState('');
  const [filtro, setFiltro] = useState<ConversaStatus | null>(null);
  /** Chave de GRUPOS_DE_CANAL; null = todos os canais. */
  const [canal, setCanal] = useState<string | null>(null);
  const [nomePorUsuario, setNomePorUsuario] = useState<Record<string, string>>({});

  // Ref para o broadcast saber, sem virar dependência do efeito, qual conversa
  // está aberta agora.
  const selecionadaRef = useRef<string | null>(null);
  useEffect(() => { selecionadaRef.current = selecionada; }, [selecionada]);

  // ── Descoberta da org ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!profile?.id) return;
    let vivo = true;
    carregarOrgId(profile.id)
      .then((id) => {
        if (!vivo) return;
        if (!id) setSemAcesso(true);
        setOrgId(id);
      })
      .catch((e) => vivo && setErroGeral(e.message))
      .finally(() => vivo && setIniciando(false));
    return () => { vivo = false; };
  }, [profile?.id]);

  // ── Busca com atraso: não dispara uma consulta por tecla ──────────────────
  useEffect(() => {
    const t = setTimeout(() => setBuscaDebounce(busca), 300);
    return () => clearTimeout(t);
  }, [busca]);

  const recarregarLista = useCallback(async () => {
    if (!orgId) return;
    setCarregandoLista(true);
    try {
      const canais = GRUPOS_DE_CANAL.find((g) => g.chave === canal)?.tipos ?? null;
      const lista = await carregarConversas({
        orgId, status: filtro, busca: buscaDebounce, meuId: profile?.id, canais,
      });
      setConversas(lista);
      if (!buscaDebounce && !filtro && !canal) setTotalConversas(lista.length);
    } catch (e: any) {
      setErroGeral(e.message ?? 'Falha ao carregar as conversas.');
    } finally {
      setCarregandoLista(false);
    }
  }, [orgId, filtro, canal, buscaDebounce, profile?.id]);

  useEffect(() => { void recarregarLista(); }, [recarregarLista]);

  // As listas de apoio da Fase 14. Falha aqui não derruba a caixa: sem
  // etiquetas a conversa continua sendo respondida, e um erro em cima da tela
  // por causa de uma lista de apoio pareceria falha no atendimento.
  useEffect(() => {
    if (!orgId) return;
    let vivo = true;
    Promise.all([
      carregarMembrosDaOrg(orgId),
      carregarEtiquetas(orgId),
      carregarRespostasRapidas(orgId),
    ])
      .then(([m, t, r]) => {
        if (!vivo) return;
        setMembros(m);
        setEtiquetas(t);
        setRespostasRapidas(r);
      })
      .catch(() => { /* a caixa funciona sem elas */ });
    return () => { vivo = false; };
  }, [orgId]);

  // Etiquetas das conversas visíveis, numa consulta só para a lista inteira.
  useEffect(() => {
    if (conversas.length === 0) { setEtiquetasPorConversa({}); return; }
    let vivo = true;
    carregarEtiquetasDasConversas(conversas.map((c) => c.id))
      .then((mapa) => vivo && setEtiquetasPorConversa(mapa))
      .catch(() => { /* a bolinha some, a conversa continua */ });
    return () => { vivo = false; };
  }, [conversas]);

  const recarregarConexoes = useCallback(async () => {
    if (!orgId) return;
    try {
      setConexoes(await carregarConexoes(orgId));
    } catch {
      /* banner some; a caixa continua funcionando */
    }
  }, [orgId]);

  useEffect(() => { void recarregarConexoes(); }, [recarregarConexoes]);

  // Heartbeat do backend marca conexão parada depois de 5 min; reler de minuto
  // em minuto mantém o banner honesto sem custo relevante.
  useEffect(() => {
    if (!orgId) return;
    const t = setInterval(() => { void recarregarConexoes(); }, 60000);
    return () => clearInterval(t);
  }, [orgId, recarregarConexoes]);

  // ── Thread ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selecionada) { setMensagens([]); return; }
    let vivo = true;
    setCarregandoThread(true);
    setErroEnvio(null);
    carregarMensagens(selecionada)
      .then((msgs) => {
        if (!vivo) return;
        setMensagens(msgs);
        setTemMaisAntigas(msgs.length >= PAGINA_THREAD);
      })
      .catch((e) => vivo && setErroGeral(e.message))
      .finally(() => vivo && setCarregandoThread(false));
    return () => { vivo = false; };
  }, [selecionada]);

  // O chamado vem da view (que já resolve SLA e contagem de conversas), não do
  // que a lista trouxe: a lista carrega o protocolo para exibir, mas não os
  // prazos nem quantas conversas respondem por ele.
  const recarregarChamado = useCallback(async (ticketId: string | null | undefined) => {
    if (!ticketId) { setChamado(null); return; }
    try {
      setChamado(await carregarChamado(ticketId));
    } catch {
      setChamado(null);
    }
  }, []);

  const abrirConversa = useCallback((id: string) => {
    setSelecionada(id);
    const c = conversas.find((x) => x.id === id);
    if (c && c.unread_count > 0 && !somenteLeituraRef.current) {
      // Otimista: o contador zera na hora e o banco confirma depois.
      setConversas((atual) => atual.map((x) => (x.id === id ? { ...x, unread_count: 0 } : x)));
      void marcarComoLida(id);
    }
  }, [conversas]);

  const carregarAntigas = useCallback(async () => {
    if (!selecionada || carregandoAntigas || mensagens.length === 0) return;
    setCarregandoAntigas(true);
    try {
      const antigas = await carregarMensagens(selecionada, mensagens[0].provider_timestamp);
      if (antigas.length === 0) { setTemMaisAntigas(false); return; }
      setMensagens((atual) => [...antigas, ...atual]);
      setTemMaisAntigas(antigas.length >= PAGINA_THREAD);
    } catch (e: any) {
      setErroGeral(e.message ?? 'Falha ao carregar o histórico.');
    } finally {
      setCarregandoAntigas(false);
    }
  }, [selecionada, carregandoAntigas, mensagens]);

  // ── Chamado da conversa aberta ────────────────────────────────────────────

  // ── Nomes dos atendentes, para assinar o balão ────────────────────────────
  useEffect(() => {
    const ids = Array.from(
      new Set(mensagens.map((m) => m.sender_user_id).filter((id): id is string => !!id && !nomePorUsuario[id])),
    );
    if (ids.length === 0) return;
    let vivo = true;
    supabase.from('profiles').select('id, full_name').in('id', ids).then(({ data }) => {
      if (!vivo || !data) return;
      setNomePorUsuario((atual) => {
        const novo = { ...atual };
        for (const p of data as { id: string; full_name: string }[]) novo[p.id] = p.full_name;
        return novo;
      });
    });
    return () => { vivo = false; };
  }, [mensagens, nomePorUsuario]);

  // ── Tempo real ────────────────────────────────────────────────────────────
  // O worker esvazia a fila em lote: cinco mensagens seguidas viram cinco
  // broadcasts em menos de um segundo. Sem agrupar, seria uma consulta da lista
  // para cada uma — o balão aparece na hora de qualquer jeito, quem espera é só
  // a prévia da esquerda.
  const timerListaRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const agendarRecargaDaLista = useCallback(() => {
    if (timerListaRef.current) clearTimeout(timerListaRef.current);
    timerListaRef.current = setTimeout(() => { void recarregarLista(); }, 400);
  }, [recarregarLista]);

  useEffect(() => () => { if (timerListaRef.current) clearTimeout(timerListaRef.current); }, []);

  const aoReceberEvento = useCallback((_evento: 'recebida' | 'enviada', payload: PayloadBroadcast) => {
    // A lista sempre relê: prévia, ordem e não lidas são do banco, e o payload
    // do broadcast é parcial de propósito.
    agendarRecargaDaLista();

    const id = payload.conversation_id;
    if (!id || id !== selecionadaRef.current) return;

    const messageId = payload.message_id ?? payload.id;
    if (!messageId) return;

    setMensagens((atual) => {
      if (atual.some((m) => m.id === messageId)) return atual;
      const nova: Mensagem = {
        id: messageId,
        conversation_id: id,
        direction: payload.direction ?? 'inbound',
        type: payload.type ?? 'text',
        body: payload.body ?? null,
        // O broadcast do Realtime carrega so o essencial: o tipo dele nem tem
        // assunto nem corpo de e-mail. Ficam nulos aqui e chegam completos na
        // recarga da thread, ja agendada acima — o e-mail aparece primeiro como
        // texto e ganha o corpo formatado um instante depois.
        subject: null,
        body_html: null,
        body_visible: null,
        recipients: null,
        provider_message_id: null,
        provider_timestamp: payload.provider_timestamp ?? new Date().toISOString(),
        status: payload.status ?? 'sent',
        failure_reason: null,
        sender_user_id: payload.sender_user_id ?? null,
        anexos: [],
      };
      // Ordena porque uma mensagem atrasada na fila pode chegar depois de uma
      // mais nova — o carimbo do provider é que manda.
      return [...atual, nova].sort(
        (a, b) => new Date(a.provider_timestamp).getTime() - new Date(b.provider_timestamp).getTime(),
      );
    });

    if (id === selecionadaRef.current && !somenteLeituraRef.current) void marcarComoLida(id);
  }, [agendarRecargaDaLista]);

  const estadoRealtime = useInboxRealtime({ orgId, onEvento: aoReceberEvento });

  // ── Envio ─────────────────────────────────────────────────────────────────
  const conversaAtual = useMemo(
    () => conversas.find((c) => c.id === selecionada) ?? null,
    [conversas, selecionada],
  );
  // Sem cair na primeira conexão da lista quando não acha a da conversa: com
  // mais de um canal ligado (o e-mail da Fase 7 já está no banco), o fallback
  // mostraria o composer com as capabilities do canal errado — campo de assunto
  // numa conversa de WhatsApp, por exemplo.
  // Só vira botão o canal que tem conexão cadastrada: oferecer "Instagram"
  // antes de a Meta liberar seria um filtro que sempre volta vazio.
  const canaisDisponiveis = useMemo(
    () => GRUPOS_DE_CANAL
      .filter((g) => conexoes.some((c) => g.tipos.includes(c.channel_type)))
      .map(({ chave, rotulo }) => ({ chave, rotulo })),
    [conexoes],
  );

  const conexaoAtual = useMemo(
    () => conexoes.find((c) => c.id === conversaAtual?.connection_id) ?? null,
    [conexoes, conversaAtual],
  );

  /**
   * Thread de comentários de post (Instagram, Fase 13).
   *
   * Não é uma variação visual: muda o endpoint do envio e liga o aviso de
   * resposta pública no composer. Vem do `kind` da conversa, não do canal —
   * `channel_type === 'instagram'` seria ramificar por canal, que é o que o
   * módulo evita, e ainda por cima erraria: o mesmo Instagram tem DM também.
   */
  const ehComentario = conversaAtual?.kind === 'comment_thread';

  // Segue a conversa aberta: trocar de conversa troca o chamado do painel.
  useEffect(() => {
    void recarregarChamado(conversaAtual?.ticket_id);
  }, [conversaAtual?.ticket_id, recarregarChamado]);

  const criarChamado = useCallback(async (dados: {
    assunto: string; prioridade: TicketPrioridade; descricao: string;
  }) => {
    if (!selecionada) return;
    setAbrindoChamado(true);
    setErroChamado(null);
    try {
      const novo = await abrirChamado({
        conversationId: selecionada,
        assunto: dados.assunto,
        prioridade: dados.prioridade,
        descricao: dados.descricao || null,
      });
      setModalChamado(false);
      // Relê pela view: o retorno da RPC é a linha crua, sem SLA calculado
      // nem a contagem de conversas vinculadas.
      await recarregarChamado(novo.id);
      await recarregarLista();
    } catch (e: any) {
      setErroChamado(e.message ?? 'Não consegui abrir o chamado.');
    } finally {
      setAbrindoChamado(false);
    }
  }, [selecionada, recarregarChamado, recarregarLista]);

  const alterarStatusChamado = useCallback(async (status: TicketStatus) => {
    if (!chamado) return;
    setOcupadoChamado(true);
    try {
      await mudarStatusChamado(chamado.id, status);
      await recarregarChamado(chamado.id);
      await recarregarLista();
    } catch (e: any) {
      setErroGeral(e.message ?? 'Não consegui mudar o status do chamado.');
    } finally {
      setOcupadoChamado(false);
    }
  }, [chamado, recarregarChamado, recarregarLista]);

  const escalarChamado = useCallback(async () => {
    if (!chamado) return;
    if (!confirm(`Escalar o chamado ${chamado.protocolo} para a TI? Isso cria uma solicitação na Central.`)) return;
    setOcupadoChamado(true);
    try {
      const tarefaId = await escalarParaTi(chamado.id);
      await recarregarChamado(chamado.id);
      setErroGeral(null);
      alert(`Solicitação #${tarefaId} criada na Central, vinculada ao ${chamado.protocolo}.`);
    } catch (e: any) {
      setErroGeral(e.message ?? 'Não consegui escalar o chamado.');
    } finally {
      setOcupadoChamado(false);
    }
  }, [chamado, recarregarChamado]);

  // ── WhatsApp completo (23/10/2026) ──
  // Anexo, voz, figurinha, responder citando e reagir. Só nas conversas de
  // WhatsApp (Evolution), que é o canal testado; e-mail e Instagram seguem
  // com o envio de texto de antes.
  const ehWhatsApp = conexaoAtual?.channel_type === 'whatsapp_evolution';
  const acoes = useMemo(
    () => (selecionada && ehWhatsApp ? criarAcoes(transporteInbox(selecionada)) : null),
    [selecionada, ehWhatsApp],
  );
  const [respondendo, setRespondendo] = useState<Mensagem | null>(null);
  useEffect(() => { setRespondendo(null); }, [selecionada]);
  const rico = useEnvioRico({
    acoes,
    conversaId: selecionada,
    userId: profile?.id ?? null,
    aplicar: setMensagens,
    aoEnviar: () => { void recarregarLista(); },
  });

  const enviar = useCallback(async (texto: string) => {
    if (!selecionada) return;
    if (acoes) {
      const citada = respondendo;
      setRespondendo(null);
      await rico.enviarTexto(texto, citada);
      return;
    }
    setEnviando(true);
    setErroEnvio(null);
    try {
      // Fase 13 — numa thread de comentários, o alvo é o ÚLTIMO comentário que
      // o cliente escreveu, não a conversa. O Instagram responde comentário por
      // `/{comment_id}/replies`: sem o id, a resposta iria para o direct dele.
      const alvoComentario = ehComentario
        ? [...mensagens].reverse().find((m) => m.direction === 'inbound' && m.provider_message_id)
            ?.provider_message_id ?? null
        : null;

      const msg = await enviarMensagem(selecionada, texto, alvoComentario);
      setMensagens((atual) => (atual.some((m) => m.id === msg.id) ? atual : [...atual, msg]));
      void recarregarLista();
    } catch (e: any) {
      setErroEnvio(e.message ?? 'Não consegui enviar.');
    } finally {
      setEnviando(false);
    }
  }, [selecionada, recarregarLista, ehComentario, mensagens, acoes, respondendo, rico]);

  const reenviar = useCallback(async (messageId: string) => {
    setReenviandoId(messageId);
    setErroEnvio(null);
    try {
      await reenviarMensagem(messageId);
      setMensagens((atual) =>
        atual.map((m) => (m.id === messageId ? { ...m, status: 'queued', failure_reason: null } : m)),
      );
    } catch (e: any) {
      setErroEnvio(e.message ?? 'Não consegui reenviar.');
    } finally {
      setReenviandoId(null);
    }
  }, []);

  // ── Estados de borda ──────────────────────────────────────────────────────
  if (iniciando) {
    return (
      <div className="flex h-[calc(100vh-10rem)] items-center justify-center">
        <Loader2 size={22} className="animate-spin text-gray-300 dark:text-slate-600" />
      </div>
    );
  }

  if (semAcesso) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-6 py-20 text-center">
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-slate-700 dark:text-slate-500">
          <InboxIcon size={24} />
        </div>
        <h1 className="text-base font-bold text-gray-800 dark:text-white">Você ainda não tem acesso à caixa de entrada</h1>
        <p className="mt-1.5 text-sm text-gray-500 dark:text-slate-400">
          O acesso a este módulo é liberado pessoa a pessoa, separado do seu perfil no sistema. Peça
          para um administrador do Inbox te incluir.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col gap-3 px-4 md:px-6">
      <header className="flex shrink-0 items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold leading-tight text-gray-900 dark:text-white">Caixa de entrada</h1>
          <p className="truncate text-sm leading-tight text-gray-500 dark:text-slate-400">
            Todas as conversas dos canais da Vigor, com o cliente do lado.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => setBuscaAberta(true)}
            title="Buscar no corpo de todas as mensagens"
            className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:text-primary dark:border-slate-700 dark:text-slate-300"
          >
            <Search size={13} />
            Buscar no histórico
          </button>
          {ehAdmin && (
            <Link
              to="/inbox/configuracoes"
              className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:text-primary dark:border-slate-700 dark:text-slate-300"
            >
              <Settings2 size={13} />
              Conexões
            </Link>
          )}
          <button
            onClick={() => { void recarregarLista(); void recarregarConexoes(); }}
            className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:text-primary dark:border-slate-700 dark:text-slate-300"
          >
            <RefreshCw size={13} className={carregandoLista ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>
      </header>

      {(conexoes.length > 0 || estadoRealtime === 'caiu') && (
        <div className="shrink-0">
          <BannerConexao
            conexoes={conexoes}
            realtime={estadoRealtime}
            onReconectar={() => { void recarregarConexoes(); void recarregarLista(); }}
          />
        </div>
      )}

      {erroGeral && (
        <div className="shrink-0 rounded-xl bg-red-50 px-3.5 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
          {erroGeral}
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 md:grid-cols-[320px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)_280px]">
        <ListaConversas
          conversas={conversas}
          selecionada={selecionada}
          onSelecionar={abrirConversa}
          busca={busca}
          onBusca={setBusca}
          filtro={filtro}
          onFiltro={setFiltro}
          canais={canaisDisponiveis}
          canal={canal}
          onCanal={setCanal}
          carregando={carregandoLista}
          total={totalConversas}
          etiquetas={etiquetas}
          etiquetasPorConversa={etiquetasPorConversa}
        />

        <Thread
          conversa={conversaAtual}
          mensagens={mensagens}
          carregando={carregandoThread}
          carregandoAntigas={carregandoAntigas}
          temMaisAntigas={temMaisAntigas}
          onCarregarAntigas={carregarAntigas}
          onReenviar={reenviar}
          reenviandoId={reenviandoId}
          nomePorUsuario={nomePorUsuario}
          onAbrirChamado={somenteLeitura ? undefined : () => { setErroChamado(null); setModalChamado(true); }}
          onResponder={acoes && !somenteLeitura ? setRespondendo : undefined}
          onReagir={acoes && !somenteLeitura ? rico.reagir : undefined}
        >
          {conversaAtual && somenteLeitura && (
            <p className="shrink-0 border-t border-gray-100 px-4 py-3 text-center text-xs text-gray-500 dark:border-slate-700 dark:text-slate-400">
              Você acompanha esta conversa só para leitura.
            </p>
          )}
          {conversaAtual && !somenteLeitura && (
            <Composer
              conexao={conexaoAtual}
              enviando={enviando || rico.enviando}
              erro={erroEnvio || rico.erro}
              respostasRapidas={respostasRapidas}
              publica={ehComentario}
              janelaExpiraEm={conversaAtual.window_expires_at}
              onEnviar={enviar}
              onLimparErro={() => { setErroEnvio(null); rico.limparErro(); }}
              onEnviarArquivos={acoes ? (itens, legenda) => {
                const citada = respondendo;
                setRespondendo(null);
                void rico.enviarArquivos(itens, legenda, citada);
              } : undefined}
              onEnviarFigurinha={acoes ? (f) => {
                const citada = respondendo;
                setRespondendo(null);
                void rico.enviarFigurinha(f, citada);
              } : undefined}
              listarFigurinhas={acoes ? acoes.listarFigurinhas : undefined}
              respondendo={respondendo ? resumoCitacao(respondendo, nomePorUsuario, conversaAtual.contato_nome) : null}
              onCancelarResposta={() => setRespondendo(null)}
            />
          )}
        </Thread>

        {/* Observador: o painel continua visível, com os controles desligados. */}
        <fieldset disabled={somenteLeitura} className="contents">
        <PainelContato
          conversa={conversaAtual}
          conexao={conexaoAtual}
          chamado={chamado}
          ocupadoChamado={ocupadoChamado}
          onMudarStatusChamado={alterarStatusChamado}
          onEscalarChamado={escalarChamado}
          /* O vínculo muda a própria linha da conversa (unidade e negociação
             vêm dela), então a lista precisa ser relida — senão o painel mostra
             o vínculo novo e o resto da tela continua no antigo. */
          onVinculoMudou={() => void recarregarLista()}
          orgId={orgId}
          meuId={profile?.id ?? null}
          membros={membros}
          etiquetas={etiquetas}
          etiquetasDaConversa={selecionada ? (etiquetasPorConversa[selecionada] ?? []) : []}
          onTrabalhoMudou={() => { void recarregarLista(); }}
        />
        </fieldset>
      </div>

      {buscaAberta && orgId && (
        <ModalBusca
          orgId={orgId}
          onAbrirConversa={abrirConversa}
          onFechar={() => setBuscaAberta(false)}
        />
      )}

      {modalChamado && conversaAtual && (
        <ModalAbrirChamado
          nomeContato={conversaAtual.contato_nome || conversaAtual.contato_telefone || 'Contato'}
          salvando={abrindoChamado}
          erro={erroChamado}
          onFechar={() => setModalChamado(false)}
          onAbrir={criarChamado}
        />
      )}
    </div>
  );
}
